import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  mode: 'pessoal' | 'empresa';
}

const Hero: React.FC<HeroProps> = ({ mode }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleDownloadClick = () => {
    setIsModalOpen(true);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 overflow-hidden bg-gradient-to-br from-[#FF6B35] via-[#4A90E2] to-[#2C5282]">
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('common.loading') === 'Loading...' ? 'Coming Soon' : 'Em Breve'}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('common.loading') === 'Loading...' 
              ? 'The MotoRush app will be available soon for iOS and Android. Stay tuned!' 
              : 'O aplicação MotoRush estará disponível em breve para iOS e Android. Fique atento às novidades!'}
          </p>
          <div className="pt-4 border-t border-gray-100">
            <a 
              href="https://wa.me/244954009112"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all duration-300 font-bold shadow-md hover:shadow-green-200"
            >
              <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.966C16.59 1.988 14.113.96 11.49.959c-5.442 0-9.866 4.372-9.87 9.802 0 1.63.45 3.224 1.302 4.633L1.87 20.887l5.51-1.442.267.159zm11.23-6.805c-.328-.164-1.94-.959-2.24-1.07-.3-.109-.52-.164-.74.164-.22.329-.85 1.07-1.04 1.29-.19.22-.38.24-.7.08-.328-.164-1.386-.51-2.64-1.627-.975-.87-1.633-1.946-1.824-2.274-.19-.329-.02-.507.143-.67.147-.146.328-.382.49-.574.164-.19.22-.328.328-.548.11-.22.055-.41-.03-.574-.08-.164-.74-1.78-.99-2.41-.247-.592-.5-.509-.685-.519-.178-.009-.383-.01-.588-.01-.205 0-.537.077-.82.383-.283.307-1.082 1.057-1.082 2.578 0 1.52 1.107 2.99 1.26 3.2 1.764 2.455 3.415 3.659 5.86 4.605 2.445.945 2.445.63 2.885.589.44-.041 1.94-.794 2.213-1.52.274-.727.274-1.348.192-1.472-.083-.124-.302-.197-.63-.362z"/>
              </svg>
              <span>{t('common.loading') === 'Loading...' ? 'Chat on WhatsApp (954009112)' : 'Falar pelo WhatsApp (954009112)'}</span>
            </a>
          </div>
        </div>
      </Modal>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} 
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Content - Left Side */}
          <div 
            className={`text-white transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
          >
            {mode === 'empresa' ? (
              <>
                <span className="bg-white/25 border border-white/40 text-[#FFE4B5] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-block mb-4">
                  {t('hero.taglineCorporate')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  {t('hero.titleCorporate')}
                </h1>
                
                <p className="text-lg md:text-xl text-white/95 mb-8 max-w-lg leading-relaxed">
                  {t('hero.descCorporate')}
                </p>

                {/* Corporate CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => scrollToSection('b2b-lead-form')}
                    className="bg-[#FFE4B5] hover:bg-white text-gray-900 font-bold px-8 py-3.5 rounded-full text-center transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-orange-500/20 text-sm"
                  >
                    {t('header.scheduleDemo')} B2B
                  </button>
                  <button 
                    onClick={() => scrollToSection('corporate-sla')}
                    className="border-2 border-white/60 hover:border-white hover:bg-white/10 text-white font-bold px-8 py-3.5 rounded-full text-center transition duration-300 transform hover:scale-105 text-sm"
                  >
                    {t('header.benefits')} SLA
                  </button>
                </div>
              </>
            ) : (
              <>
                <span className="bg-white/20 border border-white/30 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-block mb-4">
                  {t('hero.taglinePersonal')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  {t('hero.titlePersonal')}
                </h1>
                
                <p className="text-lg md:text-xl text-white/90 mb-8 max-w-lg leading-relaxed">
                  {t('hero.descPersonal')}
                </p>

                {/* Store Badges */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={handleDownloadClick}
                    className="hover:opacity-90 transition-opacity transform hover:scale-105 duration-300"
                  >
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                      alt="App Store" 
                      className="h-14"
                    />
                  </button>
                  <button 
                    onClick={handleDownloadClick}
                    className="hover:opacity-90 transition-opacity transform hover:scale-105 duration-300"
                  >
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                      alt="Google Play" 
                      className="h-14"
                    />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Visual - Right Side */}
          <div 
            className={`relative hidden md:block transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'} water-float`}
          >
           {/* Phone Mockup Placeholder */}
           <div className="relative mx-auto w-[300px] h-[600px] bg-black rounded-[50px] border-8 border-gray-900 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-6 bg-gray-900 rounded-b-xl z-20"></div>
              <div className="w-full h-full bg-white relative">
                  {/* Mock Screen Content */}
                  <div className="absolute inset-0 bg-gray-100 flex flex-col">
                      {/* Map Area */}
                      <div className="flex-1 bg-blue-50 relative p-4">
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center animate-pulse shadow-lg text-white">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                  </svg>
                              </div>
                          </div>
                      </div>
                      {/* Bottom Sheet */}
                      <div className="h-1/3 bg-white rounded-t-3xl shadow-lg p-6">
                        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
                        <h3 className="font-bold text-lg mb-2">
                            {t('common.loading') === 'Loading...' ? 'Where to today?' : 'Onde vamos hoje?'}
                        </h3>
                        <div className="flex gap-4 mt-4">
                            <div className="flex-1 bg-gray-100 p-3 rounded-xl text-center">
                                <span className="block text-2xl mb-1">🏠</span>
                                <span className="text-xs font-medium">
                                    {t('common.loading') === 'Loading...' ? 'Home' : 'Casa'}
                                </span>
                            </div>
                            <div className="flex-1 bg-gray-100 p-3 rounded-xl text-center">
                                <span className="block text-2xl mb-1">🏢</span>
                                <span className="text-xs font-medium">
                                    {t('common.loading') === 'Loading...' ? 'Work' : 'Trabalho'}
                                </span>
                            </div>
                        </div>
                      </div>
                  </div>
                  {/* Status Bar Mock */}
                  <div className="absolute top-2 right-4 text-xs font-bold w-full text-right px-6 mt-1">9:41</div>
              </div>
           </div>
           
           {/* Decor Elements */}
           <div className="absolute -top-10 -right-10 w-24 h-24 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
           <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
